import type { DiagramSummary, DashboardOverview, FolderNode, DiagramSource } from '../../domains/dashboard/models'
import { canWriteToFolder } from './folderPermissions'
import { getGravityUserRegion } from '../../hooks/useAuth'
import type { DiagramType } from '../../hooks/useDiagramInfo'
import { tryTranslateOrgChartSystemDiagramName } from './orgChartSystemDiagramI18n'

/**
 * Maps diagram type to folder domain
 */
export function getDomainForDiagramType(diagramType?: 'genogram' | 'flowchart' | 'org' | 'network'): string | null {
  switch (diagramType) {
    case 'org': return 'orgchart'
    case 'flowchart': return 'process'
    case 'genogram': return 'genogram'
    case 'network': return 'network'
    default: return null
  }
}

export const NATIVE_DIAGRAM_TYPES = ['genogram', 'flowchart', 'org', 'network'] as const
export type NativeDiagramType = (typeof NATIVE_DIAGRAM_TYPES)[number]
type WritableFolderScope = { kind: 'personal' | 'org' | 'external'; id: string } | null

export function folderMatchesDiagramType(
  folder: FolderNode | null,
  diagramType: string
): boolean {
  if (!folder) return false
  if (!NATIVE_DIAGRAM_TYPES.includes(diagramType as NativeDiagramType)) return false
  switch (diagramType) {
    case 'genogram':
      return folder.domain === 'genogram'
    case 'org':
      return folder.domain === 'orgchart'
    case 'flowchart':
      return folder.domain === 'process'
    case 'network':
      return folder.domain === 'network'
    default:
      return false
  }
}

/**
 * Get the URL to open/edit a diagram
 */
export const getDiagramHref = (d: DiagramSummary): string => {
  if (d.source === 'legacy') {
    const region = getGravityUserRegion();
    const metaEnv = ((import.meta as unknown) as { env?: Record<string, string> }).env || {} as Record<string, string>
    let base = (metaEnv?.VITE_LEGACY_APP_URL || '').replace(/\/+$/, '/')
    if (region && region !== 'us') {
      base = (metaEnv.VITE_LEGACY_APP_REGIONAL_URL || '').replace(/\/+$/, '/').replace('{region}', region);
    }
    return `${base}d/${d.id}/edit`
  }
  return `/f/${d.folderId}/d/${d.id}`
}

/**
 * Get the SVG preview URL for a legacy diagram based on user's region
 * If thumbnailUrl is provided, it will be returned directly
 * Otherwise, constructs URL from diagramId and region
 */
export const getLegacyDiagramSvgUrl = (diagramId: string, thumbnailUrl?: string | null, region?: string): string => {
  if (thumbnailUrl) {
    return thumbnailUrl
  }
  
  const metaEnv = ((import.meta as unknown) as { env?: Record<string, string> }).env || {} as Record<string, string>
  
  let baseUrl: string
  
  switch (region?.toLowerCase()) {
    case 'eu':
      baseUrl = metaEnv.VITE_LEGACY_DIAGRAM_SVG_EU || 'https://neutrino-diagram-svg-eu.s3.eu-west-1.amazonaws.com'
      break
    case 'au':
      baseUrl = metaEnv.VITE_LEGACY_DIAGRAM_SVG_AU || 'https://neutrino-diagram-svg-au.s3.ap-southeast-2.amazonaws.com'
      break
    case 'us':
    default:
      baseUrl = metaEnv.VITE_LEGACY_DIAGRAM_SVG_US || 'https://neutrino-diagram-svg.s3.amazonaws.com'
      break
  }
  
  return `${baseUrl}/${diagramId}`
}

/**
 * Get the URL to create a new diagram, optionally from a template
 */
export const getCreateDiagramHref = (
  folderId: string,
  opts?: {
    templateId?: string;
    source?: 'legacy' | 'native';
    isDemo?: boolean;
    type?: string;
    start?: 'blank' | 'template' | 'import' | 'demo' | 'shapes';
    productSource?: string;
    name?: string;
    onboardingMode?: string;
    useCase?: string;
    startView?: string;
    wizard?: 'auto' | 'skip';
    orgOptions?: boolean;
    /** StartIntent seed wire value (e.g. 'sample:flowchart-basic'). */
    seed?: string;
    /** StartIntent shape library hint (e.g. 'aws', 'base', 'kubernetes'). */
    shapeSet?: string;
    /** StartIntent attribution (e.g. 'create-new:process'). */
    src?: string;
    /** Blueprint provider dialect (aws | azure | gcp | kubernetes | generic). */
    dialect?: string;
  }
): string => {
  // TODO: Refactor this properly to use the new create diagram route
  if (opts?.source === 'legacy') {
    const isDemo = opts?.isDemo ?? false
    const region = getGravityUserRegion();
    const metaEnv = ((import.meta as unknown) as { env?: Record<string, string> }).env || {} as Record<string, string>
    let base = (metaEnv.VITE_LEGACY_APP_URL || '').replace(/\/+$/, '/')
    if (region && region !== 'us') {
      base = (metaEnv.VITE_LEGACY_APP_REGIONAL_URL || '').replace(/\/+$/, '/').replace('{region}', region);
    }
    const defaultTpl = metaEnv.VITE_LEGACY_EMPTY_TEMPALTE_ID as string | undefined
    const tpl = opts?.templateId ?? defaultTpl
    const queryParts: string[] = []
    if (!isDemo) {
      queryParts.push(`projectId=${encodeURIComponent(folderId)}`)
    }
    if (tpl) {
      queryParts.push(`templateId=${encodeURIComponent(tpl)}`)
    }
    const query = queryParts.length ? `?${queryParts.join('&')}` : ''
    return `${base}d/create${query}`
  }
  // Build query params for native diagrams
  const queryParts: string[] = []
  if (opts?.templateId) {
    queryParts.push(`templateId=${encodeURIComponent(opts.templateId)}`)
  }
  if (opts?.name) {
    queryParts.push(`name=${encodeURIComponent(opts.name)}`)
  }
  if (opts?.type) {
    queryParts.push(`type=${encodeURIComponent(opts.type)}`)
  }
  if (opts?.start) {
    queryParts.push(`start=${encodeURIComponent(opts.start)}`)
  }
  if (opts?.productSource) {
    queryParts.push(`source=${encodeURIComponent(opts.productSource)}`)
  }
  if (opts?.onboardingMode) {
    queryParts.push(`onboardingMode=${encodeURIComponent(opts.onboardingMode)}`)
  }
  if (opts?.useCase) {
    queryParts.push(`useCase=${encodeURIComponent(opts.useCase)}`)
  }
  if (opts?.startView) {
    queryParts.push(`startView=${encodeURIComponent(opts.startView)}`)
  }
  if (opts?.wizard) {
    queryParts.push(`wizard=${encodeURIComponent(opts.wizard)}`)
  }
  if (opts?.orgOptions) {
    queryParts.push('orgOptions=true')
  }
  if (opts?.seed) {
    queryParts.push(`seed=${encodeURIComponent(opts.seed)}`)
  }
  if (opts?.shapeSet) {
    queryParts.push(`shapeSet=${encodeURIComponent(opts.shapeSet)}`)
  }
  if (opts?.src) {
    queryParts.push(`src=${encodeURIComponent(opts.src)}`)
  }
  if (opts?.dialect) {
    queryParts.push(`dialect=${encodeURIComponent(opts.dialect)}`)
  }
  const query = queryParts.length ? `?${queryParts.join('&')}` : ''
  return `/f/${encodeURIComponent(folderId)}/d/new${query}`
}

/**
 * Recursively search a folder tree for a node by ID.
 */
export const findFolderById = (nodes: FolderNode[], id: string): FolderNode | undefined => {
  for (const node of nodes) {
    if (node.id === id) return node
    if (node.children && node.children.length > 0) {
      const found = findFolderById(node.children, id)
      if (found) return found
    }
  }
  return undefined
}

/**
 * Get all writable folders for a specific diagram source (legacy or native)
 * Optionally filter by domain (e.g., 'orgchart', 'process', 'genogram')
 * When domainFilter is set, only folders with that explicit domain are shown.
 */
export const getWritableFolders = (
  data: DashboardOverview,
  source: DiagramSource | 'all',
  domainFilter?: string | null,
  workspaceScope?: WritableFolderScope,
): FolderNode[] => {
  const flattenFolders = (nodes: FolderNode[]): FolderNode[] => {
    const result: FolderNode[] = []
    for (const node of nodes) {
      result.push(node)
      if (node.children && node.children.length > 0) {
        result.push(...flattenFolders(node.children))
      }
    }
    return result
  }

  const scopedRoots = (() => {
    if (workspaceScope?.kind === 'personal') {
      return {
        personal: data.personal.roots,
        org: [] as FolderNode[],
        external: [] as FolderNode[],
      }
    }
    if (workspaceScope?.kind === 'org') {
      return {
        personal: [] as FolderNode[],
        org: data.org.roots,
        external: [] as FolderNode[],
      }
    }
    if (workspaceScope?.kind === 'external') {
      const external = (data.externalOrgs || []).find((org) => org.orgId === workspaceScope.id)
      return {
        personal: [] as FolderNode[],
        org: [] as FolderNode[],
        external: external?.roots ?? [],
      }
    }
    return {
      personal: data.personal.roots,
      org: data.org.roots,
      external: (data.externalOrgs || []).flatMap((org) => org.roots),
    }
  })()

  const allFolders = [
    ...flattenFolders(scopedRoots.personal),
    ...flattenFolders(scopedRoots.org),
    ...flattenFolders(scopedRoots.external),
  ]

  const homeFolders = allFolders.filter((folder) => folder.id.startsWith('home-personal'))
  const homeHasDiagrams = homeFolders.some((folder) => (folder.diagrams?.length ?? 0) > 0)

  let writable = allFolders.filter(
    (folder) => {
      // Filter by source
      if (source !== 'all' && folder.source !== source) {
        return false
      }
      // Filter by domain if specified
      if (domainFilter !== null && domainFilter !== undefined) {
        if (folder.domain !== domainFilter) {
          return false
        }
      }
      return canWriteToFolder(folder)
    }
  )

  // Walk up from each writable folder to collect pathOnly ancestors so the
  // modal can render the folder hierarchy instead of showing shared
  // sub-folders as disconnected root entries.
  const writableIdSet = new Set(writable.map(f => f.id))
  const allFolderMap = new Map(allFolders.map(f => [f.id, f]))
  const ancestorIds = new Set<string>()
  for (const folder of writable) {
    let parentId = folder.parentId
    while (parentId) {
      if (writableIdSet.has(parentId) || ancestorIds.has(parentId)) break
      const parent = allFolderMap.get(parentId)
      if (!parent || !parent.pathOnly) break
      ancestorIds.add(parentId)
      parentId = parent.parentId
    }
  }
  const pathOnlyAncestors = Array.from(ancestorIds)
    .map(id => allFolderMap.get(id)!)
    .filter(Boolean)
  writable = [...pathOnlyAncestors, ...writable]

  // Remove entries whose parent is already in the combined list (writable +
  // pathOnly ancestors). The parent's `children` array already contains the
  // child, so the modal's recursive tree rendering will show it nested --
  // keeping it in the flat list too would cause duplicates.
  const allIncludedIds = new Set(writable.map(f => f.id))
  writable = writable.filter(f => !f.parentId || !allIncludedIds.has(f.parentId))

  // Remove empty Home options and add a virtual one only when it has content
  if (!homeHasDiagrams) {
    writable = writable.filter((folder) => !folder.id.startsWith('home-personal'))
  }

  if (source === 'legacy' || source === 'all') {
    if (homeHasDiagrams) {
      const personalOrgId = '00000000-0000-0000-0000-000000000000'
      const homeVirtual: FolderNode = {
        id: 'home-personal-legacy',
        name: 'Home',
        orgId: personalOrgId,
        ownerUserId: data.userId,
        parentId: null,
        createdAt: new Date(0).toISOString(),
        updatedAt: new Date().toISOString(),
        diagrams: [],
        children: [],
        source: 'legacy',
        readOnly: false,
        role: undefined,
      }
      // Writeability for legacy requires a role, so treat as writable here
      writable = [homeVirtual, ...writable]
    }
  }

  return writable
}

/**
 * Stored org-chart main diagram title suffix from `getDomainOnboardingDiagramName` (Dashboard).
 * Must match `FolderSidebar` / `FloatingHeader` / `DomainFolderView`.
 */
export const FULL_ORG_CHART_TITLE_SUFFIX = ' - Full Org Chart' as const

type DisplayNameT = (key: string, options?: { defaultValue?: string; folder?: string }) => string

/**
 * Map API-stored diagram names to the same display strings used in the dashboard
 * and diagram header (localized blank / demo / main org chart, etc.).
 */
export function getDiagramNameForDisplay(
  rawName: string,
  tDiagram: DisplayNameT,
  tDashboard: DisplayNameT,
): string {
  const raw = (rawName ?? '').trim()
  if (!raw) return raw
  if (raw.toLowerCase() === 'blank') {
    return tDashboard('createNew.blank', { defaultValue: 'Blank' })
  }
  if (raw.toLowerCase() === 'untitled diagram') {
    return tDiagram('header.untitledDiagram', { defaultValue: 'Untitled diagram' })
  }
  if (raw === 'Demo Org Chart') {
    return tDashboard('domainFolder.demoOrgChartDiagramTitle', { defaultValue: raw })
  }
  if (raw.endsWith(FULL_ORG_CHART_TITLE_SUFFIX)) {
    const folder = raw.slice(0, -FULL_ORG_CHART_TITLE_SUFFIX.length).trim()
    if (folder) {
      return tDashboard('domainFolder.mainOrgChartDiagramTitle', {
        folder,
        defaultValue: raw,
      })
    }
  }
  const system = tryTranslateOrgChartSystemDiagramName(raw, tDashboard)
  if (system !== undefined) return system
  return raw
}

const DIAGRAM_TYPE_DASHBOARD_KEY: Partial<Record<DiagramType, string>> = {
  genogram: 'templates.names.genogram',
  flowchart: 'templates.names.flowchart',
  mindmap: 'templates.names.mindMap',
  org: 'templates.names.orgChart',
  database: 'templates.names.database',
  uml: 'templates.names.umlClass',
}

const DIAGRAM_TYPE_FALLBACK: Record<DiagramType, string> = {
  genogram: 'Genogram',
  flowchart: 'Flowchart',
  mindmap: 'Mind Map',
  network: 'Network',
  org: 'Org Chart',
  database: 'Database',
  uml: 'UML Class',
  generic: 'Diagram',
}

export function getDiagramTypeDisplayLabel(
  tDashboard: DisplayNameT,
  tDiagram: DisplayNameT,
  type: DiagramType,
): string {
  const dashboardKey = DIAGRAM_TYPE_DASHBOARD_KEY[type]
  if (dashboardKey) {
    return tDashboard(dashboardKey, { defaultValue: DIAGRAM_TYPE_FALLBACK[type] }) as string
  }
  return tDiagram(`diagramLinkPreviewModal.types.${type}`, {
    defaultValue: DIAGRAM_TYPE_FALLBACK[type],
  }) as string
}
